import { useState, useEffect } from 'react';
import { TrendingService, TrendingItem, TrendingCreator, LocationTrend } from '../services/trendingService';

interface UseTrendingPostsResult {
  posts: TrendingItem[];
  loading: boolean;
  error: string | null;
}

export const useTrendingPosts = (): UseTrendingPostsResult => {
  const [posts, setPosts] = useState<TrendingItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const fetchPosts = async () => {
      setLoading(true);
      setError(null);
      try {
        console.log('[Trending] Fetching posts...');
        const data = await TrendingService.fetchTrendingPosts();
        console.log('[Trending] Posts fetched:', data.length);
        if (!cancelled) {
          setPosts(data);
        }
      } catch (err) {
        console.error('[Trending] Error fetching posts:', err);
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to fetch trending posts');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchPosts();

    return () => {
      cancelled = true;
    };
  }, []);

  return { posts, loading, error };
};

interface UseTrendingEventsResult {
  events: TrendingItem[];
  loading: boolean;
  error: string | null;
}

export const useTrendingEvents = (): UseTrendingEventsResult => {
  const [events, setEvents] = useState<TrendingItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const fetchEvents = async () => {
      setLoading(true);
      setError(null);
      try {
        console.log('[Trending] Fetching events...');
        const data = await TrendingService.fetchTrendingEvents();
        console.log('[Trending] Events fetched:', data.length);
        if (!cancelled) {
          setEvents(data);
        }
      } catch (err) {
        console.error('[Trending] Error fetching events:', err);
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to fetch trending events');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchEvents();

    return () => {
      cancelled = true;
    };
  }, []);

  return { events, loading, error };
};

interface UseTrendingCreatorsResult {
  creators: TrendingCreator[];
  loading: boolean;
  error: string | null;
}

export const useTrendingCreators = (): UseTrendingCreatorsResult => {
  const [creators, setCreators] = useState<TrendingCreator[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const fetchCreators = async () => {
      setLoading(true);
      setError(null);
      try {
        console.log('[Trending] Fetching creators...');
        const data = await TrendingService.fetchTrendingCreators();
        console.log('[Trending] Creators fetched:', data.length);
        if (!cancelled) {
          setCreators(data);
        }
      } catch (err) {
        console.error('[Trending] Error fetching creators:', err);
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to fetch trending creators');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchCreators();

    return () => {
      cancelled = true;
    };
  }, []);

  return { creators, loading, error };
};

interface UseLocationBasedTrendingResult {
  trends: LocationTrend[];
  loading: boolean;
  error: string | null;
}

export const useLocationBasedTrending = (userLocation?: string): UseLocationBasedTrendingResult => {
  const [trends, setTrends] = useState<LocationTrend[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const fetchLocationTrends = async () => {
      setLoading(true);
      setError(null);
      try {
        console.log('[Trending] Fetching location trends...');
        const data = await TrendingService.fetchLocationBasedTrending(userLocation);
        console.log('[Trending] Location trends fetched:', data.length);
        if (!cancelled) {
          setTrends(data);
        }
      } catch (err) {
        console.error('[Trending] Error fetching location trends:', err);
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to fetch location trends');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchLocationTrends();

    return () => {
      cancelled = true;
    };
  }, [userLocation]);

  return { trends, loading, error };
};
